import { useEffect, useId, useState } from "react";
import * as Sentry from "@sentry/react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import { formatLevelSamples } from "../../helpers/exhibitNumbering";
import {
  getNextAvailableName,
  getNumberingFormats
} from "../../helpers/string_helpers";
import useFirestoreCollection from "../../hooks/useFirestoreCollection";
import { ExhibitListFormat, ExhibitStyle } from "../../types/exhibit_formats";
import { DATABASE } from "../../types/tables-data";
import { Dropdown } from "../common/Dropdown";

const EXHIBIT_STYLE_PRESETS: { label: string; value: ExhibitStyle }[] = [
  { label: "None", value: { token: "NONE", case: "UPPER", punctuation: "." } },
  {
    label: "1., 2., 3., ...",
    value: { token: "NUMERIC", case: "UPPER", punctuation: "." }
  },
  {
    label: "I., II., III., ...",
    value: { token: "ROMAN_NUMERAL", case: "UPPER", punctuation: "." }
  },
  {
    label: "i., ii., iii., ...",
    value: { token: "ROMAN_NUMERAL", case: "LOWER", punctuation: "." }
  },
  {
    label: "A., B., C., ...",
    value: { token: "LETTER", case: "UPPER", punctuation: "." }
  },
  {
    label: "a., b., c., ...",
    value: { token: "LETTER", case: "LOWER", punctuation: "." }
  }
];

export const getStylePreviewLabel = (style: ExhibitStyle): string => {
  if (style.token === "NONE") return "None";
  // Formats authored with a prefix (e.g. "Exhibit A") can't be built from the
  // presets below, so read them back as they render rather than as bare tokens.
  if (style.prefix) {
    return `${formatLevelSamples(style).join(", ")}, ...`;
  }
  return `${getNumberingFormats(style).join(", ")}, ...`;
};

const AddNewFormatModal = ({
  isOpen,
  onClose,
  initialData,
  onFormatCreated,
  className = "z-1400"
}: {
  isOpen: boolean;
  onClose: () => void;
  initialData?: ExhibitListFormat | null;
  onFormatCreated?: (formatId: string) => void;
  /**
   * Stacking context for the dialog. Callers that open this from a Subframe
   * drawer must raise it above the drawer's own z-index.
   */
  className?: string;
}) => {
  const {
    documents: formats,
    isDocumentSaving,
    saveDocument
  } = useFirestoreCollection<ExhibitListFormat>(DATABASE.EXHIBIT_LIST_FORMATS, {
    listen: true,
    useLawyerAccessMap: true,
    enableToast: true
  });

  const formatNameId = useId();
  const documentsNames = formats?.map((doc) => doc.formatName);
  const [formatName, setFormatName] = useState("");
  const [style, setStyle] = useState<(ExhibitStyle | null)[]>([
    null,
    null,
    null,
    null
  ]);

  const handleStyleChange = (index: number, selected: ExhibitStyle) => {
    const newStyle = [...style];
    newStyle[index] = selected;
    setStyle(newStyle);
  };

  const isComplete = formatName.trim() !== "" && style.every((s) => s !== null);

  useEffect(() => {
    if (initialData) {
      setFormatName(initialData.formatName);
      setStyle(initialData.style);
    } else {
      setFormatName(
        getNextAvailableName({
          baseName: "Custom Format",
          existingNames: documentsNames ?? [],
          separator: " "
        })
      );
      setStyle([null, null, null, null]);
    }
  }, [initialData, formats]);

  const handleAddNewFormat = async () => {
    const newFormat: ExhibitListFormat = {
      ...(initialData?.id ? { id: initialData.id } : {}),
      formatName,
      style: style.filter((s): s is ExhibitStyle => s !== null),
      ...(!initialData?.id && { isNew: true })
    };

    onClose();

    setFormatName("");
    setStyle([
      EXHIBIT_STYLE_PRESETS[0].value,
      EXHIBIT_STYLE_PRESETS[3].value,
      EXHIBIT_STYLE_PRESETS[1].value,
      EXHIBIT_STYLE_PRESETS[5].value
    ]);

    try {
      const savedId = await saveDocument(newFormat);
      // Newly created formats are applied immediately so the user doesn't have
      // to hunt for them at the end of the list and apply them manually.
      if (savedId && !initialData?.id) {
        onFormatCreated?.(savedId);
      }
    } catch (err) {
      Sentry.captureException(err);
    }
  };

  const renderLabel = ({
    styleItem,
    label,
    indent,
    bold = false,
    index = 0
  }: {
    styleItem: ExhibitStyle | null;
    label: string;
    indent: number;
    bold?: boolean;
    index?: number;
  }) => {
    const text =
      !styleItem || styleItem.token === "NONE"
        ? label
        : `${getNumberingFormats(styleItem)[index] ?? ""} ${label}`;

    return (
      <span
        style={{ paddingLeft: indent * 4 }}
        className={bold ? "font-medium text-fg" : "text-fg-3"}
      >
        {text}
      </span>
    );
  };

  const handleModalClose = () => {
    if (!initialData) {
      setStyle([null, null, null, null]);
    }
    onClose();
  };

  useEffect(() => {
    if (!isOpen && !initialData) {
      setStyle([null, null, null, null]);
    }
  }, [isOpen, initialData]);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleModalClose();
      }}
    >
      <DialogContent
        className={cn(
          "w-225 max-w-[95vw] gap-0 p-0 sm:max-w-[95vw]",
          className
        )}
        onInteractOutside={(e) => {
          // The Level pickers portal their menu to <body>, so clicking an
          // option is seen as "outside" the dialog. Keep the dialog open when
          // the interaction happens inside a portaled popover.
          const target = (e.detail?.originalEvent?.target ??
            e.target) as HTMLElement | null;
          if (target?.closest?.("[data-slot=popover-content]")) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader className="border-b border-line-2 px-4 py-3">
          <DialogTitle className="text-lg leading-6">
            {initialData
              ? "Edit Exhibit List Format"
              : "Add New Exhibit List Format"}
          </DialogTitle>
        </DialogHeader>

        <div className="flex w-full items-stretch gap-8 p-6">
          <div className="flex flex-1 flex-col gap-4">
            <div className="flex w-full flex-col gap-1.5">
              <Label htmlFor={formatNameId}>Format Name</Label>
              <Input
                id={formatNameId}
                placeholder="Enter format name"
                value={formatName}
                onChange={(e) => setFormatName(e.target.value)}
                aria-invalid={!formatName.trim()}
              />
              {!formatName.trim() ? (
                <p className="text-sm text-danger">Format name is required</p>
              ) : null}
            </div>

            <Separator />

            {["Level 1", "Level 2", "Level 3", "Level 4"].map((label, i) => (
              <div key={label} className="flex w-full flex-col gap-1.5">
                <span className="text-sm font-medium text-fg">{label}</span>
                <Dropdown
                  withSearch={false}
                  list={EXHIBIT_STYLE_PRESETS.map((p) => ({
                    title: getStylePreviewLabel(p.value),
                    value: getStylePreviewLabel(p.value)
                  }))}
                  selectedValue={
                    style[i]
                      ? {
                          title: getStylePreviewLabel(style[i]!),
                          value: getStylePreviewLabel(style[i]!)
                        }
                      : undefined
                  }
                  onItemSelect={(item) => {
                    const preset = EXHIBIT_STYLE_PRESETS.find(
                      (p) => getStylePreviewLabel(p.value) === item.value
                    );
                    if (preset) handleStyleChange(i, preset.value);
                  }}
                  title="Select"
                />
              </div>
            ))}
          </div>

          <div className="flex flex-1 flex-col gap-3 rounded-lg bg-fill-1 p-4">
            <span className="text-sm font-medium text-fg-3">Preview</span>
            <div className="flex flex-1 justify-center">
              <div className="flex w-80 flex-col gap-5 rounded-md bg-elevated p-8">
                {renderLabel({
                  styleItem: style[0],
                  label: "LEVEL 1",
                  indent: 0,
                  bold: true
                })}

                {renderLabel({
                  styleItem: style[1],
                  label: "LEVEL 2",
                  indent: 4
                })}
                {renderLabel({
                  styleItem: style[1],
                  label: "LEVEL 2",
                  indent: 4,
                  index: 1
                })}
                {renderLabel({
                  styleItem: style[1],
                  label: "LEVEL 2",
                  indent: 4,
                  index: 2
                })}

                {renderLabel({
                  styleItem: style[2],
                  label: "LEVEL 3",
                  indent: 8
                })}

                {renderLabel({
                  styleItem: style[3],
                  label: "LEVEL 4",
                  indent: 12
                })}

                {renderLabel({
                  styleItem: style[0],
                  label: "LEVEL 1",
                  indent: 0,
                  bold: true,
                  index: 1
                })}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="m-0 rounded-b-xl px-4 py-3">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAddNewFormat}
            disabled={!isComplete || isDocumentSaving}
          >
            {isDocumentSaving ? <Spinner /> : null}
            {initialData ? "Save Format" : "Add New Format"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddNewFormatModal;
